import axios from "axios";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

const AppContext = createContext({});

const AppContextProvider = (props) => {
  const [selectedGarden, setSelectedGarden] = useState(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [gardens, setGardens] = useState([]);
  const [loadingGardens, setLoadingGardens] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [liDevices, setLiDevices] = useState([]);
  const [activeAlarms, setActiveAlarms] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const [bltPlansEditable, setBltPlansEditable] = useState(false);
  const [tenantType, setTenantType] = useState(null);
  const queryClient = useQueryClient()
  const [isAuthReady, setIsAuthReady] = useState(false);
  // --- notifications: paginated, infinite-scroll friendly ---
  const [notifications, setNotifications] = useState([]);
  const [notificationsPagination, setNotificationsPagination] = useState({
    page: 0,
    pages: 0,
    totalItems: 0,
  });
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationsPageRef = useRef(0);

  const NOTIFICATIONS_LIMIT = 10;
  const NOTIFICATION_BASE = process.env.REACT_APP_PLANNER_API_END_POINT + "/notification";

  const {
    data: userResData,
    refetch: userResRefetch
  } = useQuery(
    ["GetMyUser"],
    () => axios.get(`${process.env.REACT_APP_GARDENER_API_END_POINT}/user/me`),
    {
      enabled: !!token,
      refetchOnWindowFocus: false,
    }
  );

  const fetchUserData = useCallback(async () => {
    await userResRefetch();
  }, [userResRefetch]);

  const alarmStateRes = useQuery(
    ["AlarmState"],
    () => {
      return axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/tenant/alarms?resolved=false");
    },
    { enabled: !!token, refetchInterval: 30000, refetchOnWindowFocus: false }
  );

  const tenantSettingRes = useQuery(
    ["TenantSettings"],
    () => {
      return axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + `/tenant/${user.tenantId}`);
    },
    { enabled: !!token && !!user, refetchOnWindowFocus: false }
  );

  // Lightweight badge refresh — fetch one record just to read unreadCount.
  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(NOTIFICATION_BASE, {
        params: { page: 1, limit: 1 },
      });
      if (typeof res.data?.unreadCount === "number") {
        setUnreadNotificationCount(res.data.unreadCount);
      }
    } catch (error) {
      console.error("Error refreshing unread notification count:", error);
    }
  }, [NOTIFICATION_BASE]);

  // reset=true -> load page 1 fresh; otherwise append the next page.
  const fetchNotifications = useCallback(
    async ({ reset = false } = {}) => {
      try {
        setLoadingNotifications(true);
        const pageToFetch = reset ? 1 : notificationsPageRef.current + 1;

        const res = await axios.get(NOTIFICATION_BASE, {
          params: { page: pageToFetch, limit: NOTIFICATIONS_LIMIT },
        });

        const { data = [], pagination, unreadCount } = res.data;
        notificationsPageRef.current = pageToFetch;

        setNotifications((prev) => (reset ? data : [...prev, ...data]));
        if (pagination) setNotificationsPagination(pagination);
        if (typeof unreadCount === "number") setUnreadNotificationCount(unreadCount);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoadingNotifications(false);
      }
    },
    [NOTIFICATION_BASE]
  );

  const loadMoreNotifications = useCallback(() => {
    if (loadingNotifications) return;
    if (notificationsPageRef.current >= notificationsPagination.pages) return;
    fetchNotifications({ reset: false });
  }, [loadingNotifications, notificationsPagination.pages, fetchNotifications]);

  const markNotificationRead = useCallback(
    async (id) => {
      try {
        await axios.put(`${NOTIFICATION_BASE}/${id}`);
        setNotifications((prev) =>
          prev.map((n) => {
            if (n._id !== id) return n;
            if (!n.isRead) setUnreadNotificationCount((c) => Math.max(0, c - 1));
            return { ...n, isRead: true };
          })
        );
      } catch (error) {
        console.error("Error marking notification as read:", error);
        throw error;
      }
    },
    [NOTIFICATION_BASE]
  );

  const fetchNotificationProperties = useCallback(
    async (id) => {
      const res = await axios.get(`${NOTIFICATION_BASE}/${id}/properties`);
      return res.data?.data || { gardens: [], devices: [] };
    },
    [NOTIFICATION_BASE]
  );

  // Poll the unread count on an interval for the badge.
  useEffect(() => {
    if (!token) return;
    refreshUnreadCount();
    const id = setInterval(refreshUnreadCount, 30000);
    return () => clearInterval(id);
  }, [token, refreshUnreadCount]);

  useEffect(() => {
    try {
      if (!alarmStateRes?.data?.data?.data) return;
      setActiveAlarms(alarmStateRes.data.data.data.some((t) => t.resolvedAt === null));
    } catch (error) {
      console.log(`Error fetching alarms: ${error}`);
    }
  }, [alarmStateRes.data]);

  useEffect(() => {
    if (!userResData) return;
    const user = userResData.data.data;

    if (!user) {
      localStorage.removeItem('token')
      localStorage.removeItem('settings')
      window.location.href = "/login"
    }

    setUser(user);
    setRole(user.role);
    setIsAuthReady(true);

    const currentLocale = localStorage.getItem("locale");
    if (!currentLocale) {
      localStorage.setItem(
        "locale",
        user.phone.startsWith("90") ? "tr" : "en"
      );
    }
  }, [userResData]);

  useEffect(() => {
    if (!tenantSettingRes?.data) return;

    if (tenantSettingRes?.data?.data?.data?.settings) {
      localStorage.setItem("settings", JSON.stringify(tenantSettingRes?.data?.data?.data?.settings));
    }

    if (tenantSettingRes?.data?.data?.data?.type) {
      localStorage.setItem("type", tenantSettingRes?.data?.data?.data?.type);
    }

    let settings = localStorage.getItem("settings");
    if (settings) {
      settings = JSON.parse(settings);
    }

    let type = localStorage.getItem("type");
    if (type) {
      setTenantType(type);
    }

    if (process.env.REACT_APP_FAVICON) {
      const favicon = document.getElementById("favicon");
      favicon.href = settings ? settings.faviconUrl : process.env.REACT_APP_FAVICON;
    }

    if (process.env.REACT_APP_TITLE) {
      const title = document.querySelector("title");
      title.innerHTML = settings ? settings.title : process.env.REACT_APP_TITLE;
    }

    settings.manageBltPlansRemotely ? setBltPlansEditable(true) : setBltPlansEditable(false);
  }, [tenantSettingRes]);

  const fetchAllGardens = useCallback(async ({ background = false, force = false } = {}) => {
    try {
      if (!background) {
        setLoadingGardens(true);
      }
      const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/garden");
      setGardens((g) => {
        if (g.length > 0 && !force) {
          return g;
        }

        return res.data.gardens;
      });
    } catch (error) {
    } finally {
      setLoadingGardens(false);
    }
  }, []);

  const fetchAlarms = useCallback(async ({ resolved } = {}) => {
    try {
      const url = `/tenant/alarms?resolved=${resolved ? "true" : "false"}`;
      const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + url);
      setAlarms(res.data.data);
      if (!resolved) setActiveAlarms(res.data.data.some((t) => t.resolvedAt === null));
    } catch (error) {
      console.error("Error fetching alarms:", error);
    }
  }, []);

  return (
    <AppContext.Provider
      value={{
        selectedGarden,
        setSelectedGarden,
        user,
        role,
        gardens,
        fetchAllGardens,
        loadingGardens,
        token,
        setToken,
        liDevices,
        alarms,
        activeAlarms,
        fetchAlarms,
        fetchUserData,
        bltPlansEditable,
        tenantType,
        isAuthReady,
        notifications,
        notificationsPagination,
        unreadNotificationCount,
        loadingNotifications,
        fetchNotifications,
        loadMoreNotifications,
        markNotificationRead,
        refreshUnreadCount,
        fetchNotificationProperties,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

export { AppContext, AppContextProvider };
